import 'ol/ol.css';
import OLMap from 'ol/Map';
import View from 'ol/View';
import GeoJSON from 'ol/format/GeoJSON';
import { Select, DragPan, MouseWheelZoom, PinchZoom, DoubleClickZoom } from 'ol/interaction';
import Overlay from 'ol/Overlay';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { Stamen, Vector as VectorSource, OSM } from 'ol/source';
import { Circle as CircleStyle, Stroke, Fill, Style } from 'ol/style';
import { all } from 'ol/loadingstrategy';
import { fromLonLat } from 'ol/proj'
import { parseConfig } from './config-parser';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';


const configureMap = (elId) => {
    /**
     * Setup a Popup when you click on a feature
     */

    //get some elements for the popover
    var container = document.getElementById('popup-'+ elId);
    if(container === null) {
        throw new Error('Could not find element with id: popup-'+ elId);
    }
    var content = container.getElementsByClassName('popup-content')[0];
    var closer = container.getElementsByClassName('ol-popup-closer')[0];

    //Create an overlay to anchor the popup to the map.
    var overlay = new Overlay({
        element: container,
        autoPan: { margin: 50 },
        autoPanAnimation: {
            duration: 250
        }
    });
    if (closer) {
        //Add a click handler to hide the popup.
        closer.onclick = function () {
            overlay.setPosition(undefined);
            closer.blur();
            // deselect the feature so it can be re-selected (triggering the popup again)
            select.getFeatures().clear();
            return false;
        }
    }

    //Create a select feature so we can select features. 
    const select = new Select({
        hitTolerance: 10,
        style: function (feature) {
            //get the style from from layer properties
            var layer = select.getLayer(feature);
            return layer.getProperties().selectStyleFunction(feature);
        }
    });
    select.on('select', function (e) {
        //when a feature is selected display a popover
        if (e.selected.length > 0) {
            const feature = e.selected[0];
            const props = feature.getProperties();

            var layer = select.getLayer(feature);
            var layerConfig = layer.getProperties().layerConfig;

            if (layerConfig.select.title !== null) {
                content.innerHTML = '<div><strong>' + props[layerConfig.select.title] + '</strong></div>';
                layerConfig.select.fields.forEach(field => {
                    if (field.propertyType === 'link') {
                        if (props[field.property] !== null && props[field.property].length > 0) {
                            content.innerHTML += '<div><a href="' + props[field.property] + '" class="">' + field.label + '</a></div>';
                        }
                    } else if (field.propertyType === 'image') {
                        if (props[field.property] !== null && props[field.property].length > 0) {
                            content.innerHTML += '<div><img src="' + props[field.property] + '" class="img-fluid"/></div>';
                        }
                    } else {
                        content.innerHTML += '<div>' + field.label + props[field.property] + '</div>';
                    }
                });
            }

            overlay.setPosition(e.mapBrowserEvent.coordinate);
        }
    });

    var styleMap = new Map();

    //load the config
    let config = parseConfig(elId);

    /*
    * Create the map
    */
    var map;

    // see https://openlayers.org/en/latest/examples/wmts-layer-from-capabilities.html
    const configureWMTSMap = () => {
        var parser = new WMTSCapabilities();

        const wmtsSource = 'https://maps.cityofnorthbay.ca/gis4/mapcacheWMTS_BML4Assets/wmts?SERVICE=WMTS&VERSION=1.1.1&REQUEST=GetCapabilities';
        fetch(wmtsSource).then(function (response) {
            return response.text();
        }).then(function (text) {
            var result = parser.read(text);
            var options = optionsFromCapabilities(result, {
                layer: 'WMTS_BaseMap3857_l4a',
                matrixSet: 'EPSG:3857',
            });
            setupMapWithSource(new WMTS(options));
        });
    };
    

    const configureWMTSMapWithStamen = () => {
        setupMapWithSource(new Stamen({
            layer: 'toner',
        }));
    }

    const configureWMTSMapWithOSM = () => {
        setupMapWithSource(new OSM());
    }

    // confgure the map
    if(config.basemap == 'stamen'){
        configureWMTSMapWithStamen();
    } else if(config.basemap == 'osm'){
        configureWMTSMapWithOSM();
    }else{
        configureWMTSMap();
    }

    function setupMapWithSource(source) {
        map = new OLMap({
            target: elId,
            overlays: [overlay],
            interactions: [select, new DragPan(), new MouseWheelZoom(), new PinchZoom(), new DoubleClickZoom],
            layers: [
                new TileLayer({
                    source: source
                })
            ],
            view: new View({
                center: config.center,
                zoom: config.zoom,
            })
        });

        // const 
        console.log('Created Map with zoom ' + config.zoom + ' at ' + config.center);

        //Register this in our global array of maps so we can access it in bootstrap
        if (typeof window.cnbOLMap === 'undefined') {
            window.cnbOLMap = [];
        }
        window.cnbOLMap.push(map);

        //Create layers based on the map configuration
        const geoJSONLayers = [];
        config.sources.filter(l => l.type === 'geoJSONSource').forEach(layerConfig => {
            //Load GEOJSON source from http
            var vectorSource = new VectorSource({
                format: new GeoJSON({ featureProjection: 'EPSG:3857' }),
                loader: function (extent, resolution, projection) {
                    var url = layerConfig.url;
                    var xhr = new XMLHttpRequest();
                    xhr.open('GET', url);
                    var onError = function () {
                        vectorSource.removeLoadedExtent(extent);
                    };
                    xhr.onerror = onError;
                    xhr.onload = function () {
                        if (xhr.status === 200) {
                            var features = vectorSource.getFormat().readFeatures(xhr.responseText);
                            // console.log('features parsed', features);
                            vectorSource.addFeatures(features);
                        } else {
                            onError();
                        }
                    };
                    xhr.send();
                },
                strategy: all
            });

            //setup the style function used to style features
            var styleFunction = function (feature) {
                //get the property value used to determine the style
                var styleName = 'default';
                const props = feature.getProperties();
                if (layerConfig.styleProperty === null) {
                    if (layerConfig.styleName !== null) {
                        styleName = layerConfig.styleName;
                    }
                } else {
                    styleName = props[layerConfig.styleProperty];
                }

                //Lookup the style in the cache
                if (styleMap.has(styleName)) {
                    return styleMap.get(styleName).style;
                }

                let styles = null;
                //create the style and store it in the cache
                switch (feature.getGeometry().getType()) {
                    case 'Point':
                    case 'MultiPoint':
                        styles = createPointStyle(layerConfig, styleName);
                        break;
                    case 'LineString':
                    case 'MultiLineString':
                        styles = createLineStyle(layerConfig, styleName);
                        break;
                }
                //cache the style in our styleCache
                styleMap.set(styleName, styles);

                return styles.style;
            };

            //setup the selected feature style function
            var selectStyleFunction = function (feature) {
                var styleName = layerConfig.styleName;

                if (layerConfig.styleProperty !== null) {
                    const props = feature.getProperties();
                    styleName = props[layerConfig.styleProperty];
                }

                return styleMap.get(styleName).selectStyle;
            };

            //Create the vector layer
            const layer = new VectorLayer({
                source: vectorSource,
                style: styleFunction,
                projection: 'EPSG:4326'
            });

            //set the selectStyleFunction on the layer properties
            layer.setProperties({
                selectStyleFunction: selectStyleFunction,
                layerConfig: layerConfig
            })

            geoJSONLayers.push(layer);
        });

        //Add the configured layers
        geoJSONLayers.forEach(layer => {
            map.addLayer(layer);
        });
    }

    //create a point styles
    function createPointStyle(layerConfig, styleName) {
        var styleConfig = layerConfig.styles[styleName];
        var style = new Style({
            image: new CircleStyle({
                radius: 10,
                fill: new Fill({ color: styleConfig.fill }),
                stroke: new Stroke({ color: styleConfig.stroke, width: 2 })
            })
        });
        var selectStyle = new Style({
            image: new CircleStyle({
                radius: 10,
                fill: new Fill({ color: styleConfig.stroke }),
                stroke: new Stroke({ color: styleConfig.fill, width: 2 })
            })
        });
        return { style: style, selectStyle: selectStyle };
    }

    //create a line styles
    function createLineStyle(layerConfig, styleName) {
        const styleConfig = layerConfig.styles[styleName.toLowerCase()];
        if (styleConfig == null) {
            //Use a default
            styleConfig = {
                stroke: 0,
                width: 4
            }
        }
        let style = new Style({
            stroke: new Stroke({
                color: styleConfig.stroke,
                width: styleConfig.width
            })
        });

        var selectStyle = new Style({
            stroke: new Stroke({
                color: styleConfig.stroke,
                width: styleConfig.width * 2
            })
        });
        return { style: style, selectStyle: selectStyle };
    }
};

var maps = document.getElementsByClassName('olmap');
for (var i = 0; i < maps.length; i++) {
    const map = maps[i];
    const id = map.getAttribute('id');
    configureMap(id);
}
